<template>
  <timeline-step
    :ref="`step-${step.id}`"
    :step="step"
    :dusk="`timeline-step-${step.id}`"
    @toggleStep="toggleStep(step.id)"
  >
    <div class="mt-4 sub-heading text-xl">Story</div>
    <div class="mt-2" v-html="campaign.story" />

    <div class="mt-4 sub-heading text-xl">Collaboration Terms</div>
    <div v-if="hasContentTheme" class="mt-4 flex flex-col">
      <strong>Content theme </strong>
      <p style="white-space: pre-line">{{ campaign.content_theme }}</p>
    </div>
    <div v-if="hasRequirements" class="relative mt-4">
      <div
        class="relative mt-4 campaign-requirements-preview overflow-hidden"
        :class="{
          'h-auto campaign-requirements-preview-open': campaignRequirementsOpen,
          'h-20': !campaignRequirementsOpen,
        }"
        v-html="campaign.technical_requirements"
      />
      <div
        class="relative mt-4 campaign-requirements-preview overflow-hidden"
        :class="{
          'hidden h-20': !campaignRequirementsOpen,
          'h-auto campaign-requirements-preview-open': campaignRequirementsOpen,
        }"
        v-html="campaign.guidelines"
      />
      <div v-if="!campaignRequirementsOpen" class="flex justify-center items-center mt-4">
        <button class="button button-primary button-sm" @click="readMore">Read more...</button>
      </div>
    </div>

    <div class="mt-4 flex justify-between items-center leading-tight">
      <strong>Apply by</strong>
      <span class="font-montserrat text-xl">
        {{ formattedApplyBy }}
      </span>
    </div>

    <div class="flex justify-between items-center leading-tight">
      <strong>Submit content by</strong>
      <span class="font-montserrat text-xl">
        {{ formattedSubmitBy }}
      </span>
    </div>

    <div v-if="campaign.reward_type !== 'no'" class="mt-4 sub-heading text-xl">Reward</div>

    <div v-if="campaign.reward_type !== 'no'" class="flex justify-between items-center leading-tight">
      <strong>Product Value</strong>
      <div class="text-primary-900 text-xl font-bold font-montserrat">${{ campaign.reward_value }}</div>
    </div>

    <div v-if="isPaidCampaign">
      <div class="flex justify-between items-center leading-tight">
        <div v-if="isPaidFlatRateCampaign || (!isPaidFlatRateCampaign && hasPayment)">
          <strong>Offered Payment</strong>
        </div>
        <div class="text-primary-900 text-xl font-bold font-montserrat">
          <div v-if="isPaidFlatRateCampaign">${{ campaign.flat_rate }}</div>
          <div v-if="!isPaidFlatRateCampaign && hasPayment">${{ campaignApplication.payment }}</div>
        </div>
      </div>

      <p v-if="!creator.paypal_address" class="w-full mt-4">
        <svg-vue icon="exclamation-mark" class="inline h-4"></svg-vue>
        Your <strong>PayPal address</strong> is required for this campaign. Go to your profile and
        <a href="/profile/edit#payment"> set it.</a>
      </p>
    </div>

    <div v-if="photosWeWant.length || (campaign.social_media_links && campaign.social_media_links.length)">
      <div class="sub-heading text-xl mt-5">Content we like</div>
      <div class="grid grid-cols-1 gap-2">
        <div v-for="(name, index) in photosWeWant" :key="index">
          <img class="max-w-full" :src="name" />
        </div>
      </div>
      <div v-for="(link, index) in campaign.social_media_links" :key="index" class="w-56 md:w-full truncate">
        <a :href="socialMediaLink(link)" target="_blank">
          {{ link }}
        </a>
      </div>
    </div>

    <div v-if="joined">
      <gift-card-code
        v-if="campaign.reward_type === 'gift_card'"
        :code="campaignApplication.gift_card_code"
        :expiration_date="campaignApplication.gift_card_expiration_date"
        :orders="campaignApplication.orders"
        :gift_cards="campaignApplication.gift_cards"
      />
    </div>

    <div v-else-if="isPendingContract || isNegotiating" class="mt-4">
      <div class="md:hidden">
        <a
          :href="`/campaign-applications/${campaignApplication.id}/collaboration-terms`"
          target="blank"
          class="underline font-bold"
          >Collaboration terms</a
        >
        <label class="mt-1 flex items-center">
          <input v-model="agreedToContract" type="checkbox" class="form-checkbox" />
          <span class="ml-2 text-black normal-case text-base">I read &amp; agree with the conditions.</span>
        </label>
      </div>

      <div class="hidden md:block">
        <div class="flex items-center">
          <input id="agreeToContract" v-model="agreedToContract" type="checkbox" class="form-checkbox" />
          <label class="ml-2 text-black normal-case text-base" for="agreeToContract">I agree with the</label>
          <a
            :href="`/campaign-applications/${campaignApplication.id}/collaboration-terms`"
            target="blank"
            class="underline ml-1 font-bold"
            >collaboration terms</a
          >
        </div>
      </div>

      <div class="mt-4 mb-4 flex justify-center">
        <button @click="agreeToContract">Accept</button>
      </div>
    </div>

    <div
      v-else-if="campaignApplyByPassed && !joined && !applied"
      class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-gray-600 font-bold text-sm"
    >
      Applications are closed
    </div>

    <div v-else-if="applied">
      <div class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-blue-600 font-bold text-sm">
        Thank you for applying for this campaign! Our team will review your application &amp; get back to you soon!
      </div>
      <div class="block text-center">
        To withdraw your application from the campaign, simply click "cancel your application" below.
      </div>
      <div class="text-right underline cursor-pointer font-semibold" @click="debouncedCancelApplication">
        Cancel Application
      </div>
    </div>
    <campaign-restrictions v-else-if="showRestrictions" class="mt-4 font-bold" />
    <apply-form v-else-if="!campaignApplyByPassed && !joined && !applied" />
  </timeline-step>
</template>

<script>
import { format, parseISO } from 'date-fns';
import { mapGetters, mapState, mapMutations } from 'vuex';
import URI from 'urijs';
import { Bus } from '../../bus';
import { gtmEvent } from '../../services/gtm';
import Swal from 'sweetalert2';
import debounce from 'lodash/debounce';

export default {
  data() {
    return {
      campaignRequirementsOpen: false,
      agreedToContract: false,
      debouncedCancelApplication: this.cancelApplication,
    };
  },
  props: ['step'],
  computed: {
    ...mapState('timeline', ['campaign', 'isEligibleForCampaign', 'creator', 'photosWeWant', 'campaignApplication']),
    ...mapGetters('timeline', [
      'applied',
      'joined',
      'instagramHandle',
      'preApprovalCampaign',
      'isPaidCampaign',
      'isPaidFlatRateCampaign',
      'isPendingContract',
      'isNegotiating',
      'hasPayment',
      'campaignApplyByPassed',
    ]),
    formattedApplyBy() {
      if (!this.campaign.apply_by) {
        return null;
      }

      return format(parseISO(this.campaign.apply_by), 'MMM dd, yyyy');
    },
    formattedSubmitBy() {
      if (
        !(this.campaignApplication?.extended_submit_by
          ? this.campaignApplication.extended_submit_by
          : this.campaign.submit_by)
      ) {
        return null;
      }

      return format(
        parseISO(
          this.campaignApplication?.extended_submit_by
            ? this.campaignApplication.extended_submit_by
            : this.campaign.submit_by,
        ),
        'MMM dd, yyyy',
      );
    },
    hasContentTheme() {
      return this.campaign.content_theme !== null && this.campaign.content_theme !== '';
    },
    hasRequirements() {
      return this.campaign.technical_requirements !== null && this.campaign.technical_requirements !== '';
    },
    showRestrictions() {
      return !this.isEligibleForCampaign && !this.campaignApplication;
    },
  },
  created() {
    this.debouncedCancelApplication = debounce(this.cancelApplication, 300, {
      leading: true,
      trailing: false,
    });
  },
  methods: {
    ...mapMutations('timeline', ['markStepCompleted']),
    socialMediaLink(link) {
      return URI(link).absoluteTo('https://');
    },
    async cancelApplication() {
      const res = await Swal.fire({
        title: '<div class="text-lg">Cancel Application</div>',
        html: "<div class='font-montserrat text-black'>Upon confirmation, your application will be immediately deleted, allowing you to apply for other available campaigns.<br><br>Please note that you can only cancel your application while it is pending review.</div>",
        confirmButtonText: '<div class="text-sm">Yes, I am sure.</div>',
        confirmButtonColor: '#813571',
        cancelButtonText: '<div class="text-sm">Cancel</div>',
        showCancelButton: true,
        showClass: {
          popup: 'swal2-noanimation',
          backdrop: 'swal2-noanimation',
        },
      });

      if (!res.value) {
        return;
      }

      await window.axios.post(`/campaigns/${this.campaign.id}/cancel`);
    },
    async agreeToContract() {
      const { data } = await window.axios.post(
        `/campaign-applications/${this.campaignApplication.id}/agree-to-contract`,
        {
          agreedToContract: this.agreedToContract,
        },
      );

      if (data && data.success) {
        this.markStepCompleted(this.step.id);

        Bus.$emit('joinedCampaign', {
          campaignApplication: data.campaignApplication,
          campaignOrderGuidelines: data.campaignOrderGuidelines,
        });
      }
    },
    readMore() {
      this.campaignRequirementsOpen = !this.campaignRequirementsOpen;

      gtmEvent('read_more', {
        ga_category: 'campaign',
        ga_action: 'read_more',
        ga_label: 'brief',
      });
    },
  },
};
</script>
